<template>
  <v-data-table
    v-model="selectedItems"
    v-model:expanded="expanded"
    v-model:sortBy="sortArray"
    v-on:[rowClickedEventName]="handleRowClick"
    v-model:items-per-page="tableItemsPerPage"
    :customFilter="customFilter ? customFilter : defaultFilter"
    :headers="filteredHeaders"
    :height="tableHeight"
    :hideDefaultFooter="hideDefaultFooter"
    :hideDefaultHeader="isMobileVersion"
    :id="tableId"
    :items="items"
    :itemValue="itemKey"
    :loading="loading"
    :rowProps="rowProps"
    :search="search"
    :showExpand="showExpand"
    :showSelect="showSelect"
    :hover="hasHover"
    class="bm-table"
    fixedHeader
    mustSort
    returnObject
    @update:expanded="isMobileVersion ? null : emit('itemExpanded', $event)"
    @update:currentItems="emit('currentItems', $event)"
    @update:sortBy="handleSortChange"
  >
    <!-- Slot for the top area of the table. No restrictions apply even though this need to be structured to save
        development time on the ux side -->

    <template #top>
      <div class="title-slot px-3 px-sm-2">
        <slot name="titleSlot" />
      </div>

      <v-expansion-panels
        v-if="(isMobileVersion && tableFilters.length) || collapsedFilters"
      >
        <v-expansion-panel>
          <div class="d-flex justify-space-between align-center">
            <v-expansion-panel-title hideActions class="py-4">
              <v-badge
                bordered
                color="primary"
                textColor="white"
                :content="getCountOfSelectedFilters"
                :modelValue="getCountOfSelectedFilters > 0"
              >
                <v-btn
                  color="primary"
                  variant="outlined"
                  data-cc="filters-chip"
                >
                  <span>{{ $t('generic.filters') }}</span>
                </v-btn>
              </v-badge>
            </v-expansion-panel-title>

            <div class="d-flex ga-2 mr-4">
              <slot name="tableActions" />

              <BMTableShowColumnsMenu
                v-if="isColumnsCustomizerVisible"
                :headers="headers"
                :tableId="tableId"
                @setColumns="showColumns = $event"
              />
            </div>
          </div>

          <v-expansion-panel-text class="pt-5">
            <v-row>
              <v-col
                v-for="(filter, index) in tableFilters"
                :key="`filter-component-${index}`"
                cols="12"
                data-cc="filter-component"
              >
                <component
                  :is="filter.component"
                  v-bind="filter.props"
                  v-on="filter.events"
                />
              </v-col>

              <BMTableClearFiltersButton
                :disabled="!getCountOfSelectedFilters"
                :collapsedFilters="collapsedFilters"
                @clear="emit('clearFilters')"
              />
            </v-row>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-container
        v-if="!isMobileVersion && tableFilters.length && !collapsedFilters"
        class="pa-6"
        fluid
        data-cc="table-filters"
      >
        <v-row>
          <v-col cols="auto" class="flex-grow-1">
            <v-row v-if="!collapsedFilters" dense>
              <v-col
                v-for="(filter, index) in tableFilters"
                :key="`filter-component-${index}`"
                :sm="getFilterColProps(filter.component)"
              >
                <component
                  :is="filter.component"
                  v-bind="filter.props"
                  v-on="filter.events"
                />
              </v-col>

              <BMTableClearFiltersButton
                :disabled="!getCountOfSelectedFilters"
                :tableId="tableId"
                @clear="emit('clearFilters')"
              />
            </v-row>
          </v-col>

          <v-col cols="auto" class="text-end" data-cc="table-actions">
            <div class="d-flex align-center justify-end ga-2">
              <slot name="tableActions" />

              <BMTableShowColumnsMenu
                v-if="isColumnsCustomizerVisible"
                :headers="headers"
                :tableId="tableId"
                @setColumns="showColumns = $event"
              />
            </div>
          </v-col>
        </v-row>

        <v-row class="pt-2 pt-sm-0" v-if="$slots.postFilterSlot">
          <v-col cols="12">
            <slot name="postFilterSlot" />
          </v-col>
        </v-row>
      </v-container>

      <v-divider v-if="isTopDividerActive" />
    </template>

    <template
      v-if="isMobileVersion"
      #body="{ items, isExpanded, toggleExpand }"
    >
      <BMSelect
        v-if="sortableColumns.length"
        class="ma-5"
        :items="sortableColumns"
        :inputValue="sortBy"
        :clearable="false"
        :isSortDesc="isSortDescending"
        isTableSortSelect
        identifier="mobile-table-sort-select"
      >
        <template #item="{ item }: any">
          <v-list-item
            class="px-7 py-2"
            :class="{
              'bg-bm_blue_light text-primary': item.value === sortBy
            }"
            @click="mobileSortChanged(item.value)"
          >
            <span>
              {{ item.title }}
            </span>
            <v-icon v-if="item.value === sortBy" class="ml-1">
              {{ sortingIcon }}
            </v-icon>
          </v-list-item>
        </template>
      </BMSelect>

      <v-card
        v-for="(item, index) in items"
        v-bind="rowProps({ item })"
        :key="`mobile-table-item-${index}`"
        :class="{ 'mt-0': !index }"
        class="row ma-4 pa-5 text-body-2 d-flex flex-column ga-3"
        elevation="0"
        border
        rounded
        data-cc="table-mobile-card"
        @click="emit('rowClicked', item)"
      >
        <div
          v-for="{ title, key } in headers"
          :key="key"
          class="mobile-field d-flex align-center ga-3"
          :class="{
            'actions-cell': !title
          }"
        >
          <div data-cc="table-mobile-field-title">
            {{ title }}
          </div>

          <v-spacer />

          <div
            v-if="!isColumnCustom(key)"
            class="font-weight-bold"
            data-cc="table-mobile-field-value"
          >
            {{ dataOrDash(item, key) }}
          </div>
          <div
            v-else
            class="font-weight-medium"
            data-cc="table-mobile-field-slot"
          >
            <slot :name="key" :item="item" />
          </div>
        </div>

        <div v-if="showExpand">
          <div
            class="show-details-btn text-caption font-weight-bold"
            @click="toggleMobileItemExpand(toggleExpand, item)"
          >
            {{ isExpanded(item as any) ? 'Hide' : 'Show' }}
          </div>

          <slot
            v-if="isExpanded(item as any)"
            name="expanded-row"
            :item="item"
          />
        </div>
      </v-card>

      <p
        v-if="!items.length"
        class="v-data-table-rows-no-data bg-bm_hover_blue mt-4 py-4"
      >
        {{ $t('statistics.noAvailableData') }}
      </p>
    </template>

    <template
      v-for="column in customColumns"
      :key="column"
      v-slot:[`item.${column}`]="slotProps: any"
    >
      <slot
        :name="column"
        v-bind="slotProps"
        :item="slotProps.item.raw || slotProps.item"
      />
    </template>

    <template #expanded-row="{ item }">
      <slot name="expanded-row" :item="item" />
    </template>

    <!-- Loop through custom headers -->
    <template
      v-for="(item, customHeaderKey) in customHeaders"
      #[`header.${item}`]="{ column }"
      :key="customHeaderKey"
    >
      <BMTableHeader
        :key="item"
        isCustomHeader
        :column="column"
        :sortValues="sortArray[0]"
      >
        <slot :name="`header.${item}`" />
      </BMTableHeader>
    </template>

    <!-- Loop through the rest headers -->
    <template
      v-for="(item, key) in headers
        .map((header) => header.key)
        .filter((header) => !customHeaders.includes(header))"
      #[`header.${item}`]="{ column }"
      :key="key"
    >
      <BMTableHeader :key="item" :column="column" :sortValues="sortArray[0]" />
    </template>

    <template v-if="!disablePagination" #bottom>
      <div
        v-if="paginationData.totalElements > tableItemsPerPage"
        class="d-flex flex-column flex-sm-row align-center justify-space-between px-6 py-5 ga-4 border-t"
      >
        <div
          class="text-body-1 text-disabled text-center text-sm-left"
          data-cc="pagination-info"
        >
          <template v-if="paginationInfo.hasItems">
            <template v-if="paginationInfo.isPaginated">
              {{ $t('generic.pagination.showing') }}

              <span
                class="font-weight-medium"
                data-cc="pagination-info-page-start"
              >
                {{ paginationInfo.start }}
              </span>
              -
              <span
                class="font-weight-medium"
                data-cc="pagination-info-page-end"
              >
                {{ paginationInfo.end }}
              </span>

              {{ $t('generic.pagination.of') }}

              <span
                class="font-weight-medium"
                data-cc="pagination-info-current-total"
              >
                {{ paginationInfo.currentTotal }}
              </span>

              {{ $t('generic.pagination.items') }}
            </template>

            <template v-else>
              <span
                class="font-weight-medium"
                data-cc="pagination-info-current-total"
              >
                {{ paginationInfo.currentTotal }}
              </span>

              {{ $t('generic.pagination.items') }}
            </template>
          </template>
        </div>

        <v-pagination
          v-model="page"
          :length="Math.ceil(paginationData.totalElements / tableItemsPerPage)"
          :totalVisible="isMobileVersion ? 2 : 7"
          :size="isMobileVersion ? 'small' : 'default'"
          density="comfortable"
          data-cc="table-pagination"
        >
          <template #item="{ props, page, isActive }">
            <v-btn
              v-bind="props"
              :variant="isActive ? 'flat' : 'tonal'"
              :color="isActive ? 'primary' : undefined"
              :size="isMobileVersion ? 'small' : 'default'"
              density="comfortable"
            >
              {{ page }}
            </v-btn>
          </template>
        </v-pagination>
      </div>
    </template>
  </v-data-table>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import { ref, computed, watch, useSlots, onActivated } from 'vue';
import { useDisplay } from 'vuetify';
import { pickBy, get, isDate } from 'lodash';

import BMSelect from '@/components/common/utilities/table/BMSelect.vue';
import BMTableClearFiltersButton from './BMTableClearFiltersButton.vue';
import BMTableShowColumnsMenu from './BMTableShowColumnsMenu.vue';
import BMTableHeader from './BMTableHeader.vue';

import { queryParamsHandler } from '@/utils';
import constants from '@/config/constants';
import trackingEvents from '@/config/trackingEvents';

import type { Pagination } from '@/types/api/pagination';

type SortItem = { key: string; order?: boolean | 'asc' | 'desc' };

// Props
interface Props<T extends Record<string, any>> {
  headers?: any[];
  items?: T[];
  customColumns?: string[];
  customHeaders?: string[];
  search?: string;
  tableId?: string;
  customFilter?: (value: any, query: string, item: any) => boolean;
  customSortColumns?: Record<string, any> | null;
  rowClasses?: ((item: T) => Record<string, boolean>) | null;
  showSelect?: boolean;
  selected?: T[];
  tableHeight?: string;
  disablePagination?: boolean;
  paginationData?: Omit<Pagination<any>, 'content'>;
  loading?: boolean;
  tableFilters?: any[];
  urlParamsSyncedTableName?: string | null;
  initialSortDesc?: boolean;
  noDefaultSorting?: boolean;
  itemKey?: string;
  showExpand?: boolean;
  collapsedFilters?: boolean;
  isRowClickable?: boolean;
  initialSort?: SortItem | null;
  hideDefaultFooter?: boolean;
  showTopDivider?: boolean;
  hasHover?: boolean;
}

interface PreparedPagination {
  start: number;
  end: number;
  currentTotal: number;
  absoluteTotal: number;
  totalPages: number;
  isFiltered: boolean;
  hasItems: boolean;
  isPaginated: boolean;
}

const props = withDefaults(defineProps<Props<T>>(), {
  headers: () => [],
  items: () => [],
  customColumns: () => [],
  customHeaders: () => [],
  search: '',
  tableId: '',
  customSortColumns: null,
  rowClasses: null,
  showSelect: false,
  selected: () => [],
  tableHeight: '80vh',
  disablePagination: true,
  paginationData: () => ({}) as Pagination<any>,
  loading: false,
  tableFilters: () => [],
  urlParamsSyncedTableName: null,
  initialSortDesc: false,
  noDefaultSorting: false,
  itemKey: 'id',
  showExpand: false,
  collapsedFilters: false,
  isRowClickable: false,
  initialSort: null,
  hideDefaultFooter: true,
  hasHover: true
});

// Emits
const emit = defineEmits([
  'clearFilters',
  'update:page',
  'rowClick',
  'selectedItems',
  'itemExpanded',
  'currentItems',
  'rowClicked',
  'sortChanged',
  'mobileItemExpanded'
]);

defineSlots<{
  titleSlot: () => any;
  tableActions: () => any;
  postFilterSlot: () => any;
  'expanded-row': (props: { item: T }) => any;
  [key: `header.${string}`]: (props: any) => any;
  [key: string]: (props: any) => any;
}>();

// Libs
const display = useDisplay();

// format is [{ key: 'string', order: 'asc' | 'desc' }]
const sortArray = ref<SortItem[]>([]);
const showColumns = ref<string[]>([]);
const expanded = ref<string[]>([]);
const rowClickedEventName = props.isRowClickable ? 'click:row' : null;

const isMobileVersion = computed(() => display.xs.value);

// Pagination
// Create a local ref to hold the v-data-table items-per-page value, where -1 shows all items by default
const clientItemsPerPage = ref(-1);

const tableItemsPerPage = computed({
  get() {
    // If client-side, return the "all items" by default
    if (props.disablePagination) {
      return clientItemsPerPage.value;
    }

    // If server-side, read from the prop (fallback to default if undefined)
    return props.paginationData?.size || constants.table.DEFAULT_NR_OF_ROWS;
  },

  set(newValue: number) {
    if (props.disablePagination) {
      // Client-side: Let Vuetify update the local ref freely
      // Server side is handled via the update:page event
      clientItemsPerPage.value = newValue;
    }
  }
});

const paginationInfo = computed((): PreparedPagination => {
  const data = props.paginationData;
  const items = props.items || [];
  const hasItems = items.length > 0;

  // Determine if this is a paginated response or just a raw array
  // We check for 'totalPages' specifically as it's the signature of a paginated API
  const isPaginated =
    data !== undefined && data !== null && data.totalPages !== undefined;

  // Counts
  const currentTotal = isPaginated
    ? (data?.totalElements ?? items.length)
    : items.length;
  const absoluteTotal = currentTotal;
  const totalPages = isPaginated ? (data?.totalPages ?? 1) : 1;

  // Range Math
  const currentPage = data?.page ?? 0;
  const currentSize = data?.size ?? tableItemsPerPage.value;

  const start = hasItems ? currentPage * currentSize + 1 : 0;
  const end = hasItems ? Math.min(start + items.length - 1, currentTotal) : 0;

  return {
    start,
    end,
    currentTotal,
    absoluteTotal,
    totalPages,
    isFiltered: isPaginated && absoluteTotal > currentTotal,
    hasItems,
    isPaginated
  };
});

// Initialize the value when the component is activated
onActivated(() => {
  if (props.disablePagination) {
    clientItemsPerPage.value = -1;
  }
});

// Sorting
const sortBy = computed(() => {
  const arr = sortArray.value;
  if (!Array.isArray(arr) || !arr.length) return '';
  return arr[0]!.key;
});

const sortOrder = computed((): boolean | 'desc' | 'asc' | undefined => {
  const arr = sortArray.value;
  if (!Array.isArray(arr) || !arr.length)
    return constants.table.ASCENDING as 'asc';
  return arr[0]!.order;
});

const isSortDescending = computed(
  () => sortOrder.value !== constants.table.ASCENDING
);

const isColumnsCustomizerVisible = computed(() => {
  const isNotDefaultHeaderExist = props.headers.find((h) => !h.default);
  return props.tableId && !isMobileVersion.value && isNotDefaultHeaderExist;
});

const filteredHeaders = computed((): any[] => {
  const headersWithSortFunctions = props.headers.map((header) => {
    const { key } = header;
    const sortFunction = props.customSortColumns?.hasOwnProperty(key)
      ? props.customSortColumns[key]
      : defaultSort;
    return { ...header, sortRaw: sortFunction };
  });

  if (!props.tableId) return headersWithSortFunctions;
  const filteredColumns = headersWithSortFunctions.filter((header) =>
    showColumns.value.includes(header.key)
  );
  return [
    ...filteredColumns,
    {
      title: '',
      key: 'customize',
      sortable: false,
      align: 'right',
      width: '80px'
    }
  ];
});

const sortingIcon = computed(() =>
  isSortDescending.value ? '$arrowdown' : '$arrowup'
);

const getCountOfSelectedFilters = computed(() =>
  props.tableFilters.reduce((counter, filter) => {
    const { modelValue, inputValue, inputDates } = filter.props;
    const filterValue = modelValue || inputValue || inputDates;
    const isNotEmptyString = typeof filterValue === 'string' && filterValue;
    const isNotEmptyDate =
      typeof filterValue === 'object' && isDate(filterValue);
    const isNotEmptyArray =
      typeof filterValue === 'object' &&
      Array.isArray(filterValue) &&
      filterValue.length;
    if (
      isNotEmptyString ||
      isNotEmptyArray ||
      isNotEmptyDate ||
      (typeof filterValue === 'boolean' && filterValue) ||
      (typeof filterValue === 'number' && filterValue)
    ) {
      counter++;
    }
    return counter;
  }, 0)
);

const sortableColumns = computed(() =>
  props.headers
    .filter(({ sortable }) => sortable ?? true)
    .map(({ key, title }) => ({ value: key, title: title }))
);

const page = computed({
  get() {
    return props.paginationData.page + 1;
  },
  set(newPage: number) {
    emit('update:page', newPage - 1);
  }
});

const selectedItems = computed({
  get() {
    return props.selected;
  },
  set(items) {
    emit('selectedItems', items);
  }
});

const handleRowClick = (_: any, { item }: { item: any }) => {
  const rawItem = item.raw || item;
  emit('rowClicked', rawItem);
};

const toggleMobileItemExpand = (toggleExpand: Function, item: any) => {
  emit('mobileItemExpanded', item);
  toggleExpand(item);
};

const rowProps = ({ item }: { item: any }) => {
  // Get the raw item accurately
  const rawItem = item.raw || item;

  // Determine selection state. Use some as there may be more than one item
  const isSelected = selectedItems.value.some(
    (s) =>
      (s?.[props.itemKey] ?? s?.id) === (rawItem[props.itemKey] ?? rawItem.id)
  );

  // Handle custom row classes from props
  let customClasses = {};
  if (props.rowClasses) {
    // Pick only truthy values from the generator
    customClasses = pickBy(props.rowClasses(rawItem), Boolean);
  }

  // Merge selection state with custom classes
  return {
    class: {
      selected: isSelected,
      ...customClasses
    }
  };
};

const handleSortChange = (sortByArray: any) => {
  trackingEvents.sortingChanged(sortByArray);
  emit('sortChanged', sortByArray);
};

const applyQueryParams = () => {
  const params = queryParamsHandler.getQueryParams();
  if (params) {
    const key = (params[`${props.urlParamsSyncedTableName}_sort_by`] ??
      sortBy.value) as string;

    let order: any = params[`${props.urlParamsSyncedTableName}_sort_desc`];
    if (order) {
      const { ASCENDING, DESCENDING } = constants.table;
      order = order === 'true' ? (DESCENDING as 'desc') : (ASCENDING as 'asc');
    } else {
      order = sortOrder.value;
    }

    sortArray.value = [{ key, order }];
  }
};

const isColumnCustom = (column: any) => props.customColumns.includes(column);

const dataOrDash = (item: any, key: any) => get(item, key, '-');

// handle sorting for mobile: if we sort by the same key as  last time
// then we reverse the order, otherwise setting 'asc' by default
const mobileSortChanged = (sortByVal: any) => {
  const { ASCENDING, DESCENDING } = constants.table;
  const order =
    sortByVal === sortBy.value && sortOrder.value === ASCENDING
      ? (DESCENDING as 'desc')
      : (ASCENDING as 'asc');

  sortArray.value = [{ key: sortByVal, order }];
};

const defaultSort = (a: any, b: any) => {
  // update query params
  if (props.urlParamsSyncedTableName) {
    queryParamsHandler.updateQueryParams({
      [`${props.urlParamsSyncedTableName}_sort_by`]: sortBy.value
    });
    queryParamsHandler.updateQueryParams({
      [`${props.urlParamsSyncedTableName}_sort_desc`]: isSortDescending.value
    });
  }

  // sanitize values to set null and undefined to empty string
  const valueToString = (value: any) =>
    value !== null && typeof value !== 'undefined' ? value.toString() : '';

  // basic sorting function {numeric: true} handles the numbers even when it's of type string
  const sorting = (a: any, b: any) => {
    return valueToString(a).localeCompare(valueToString(b), undefined, {
      numeric: true
    });
  };
  const key = sortBy.value as keyof typeof a;
  return sorting(a[key], b[key]);
};

const defaultFilter = (value: any, search: string, item: any) => {
  const searchArray = [...Object.values(item.raw ?? item)];
  for (let index = 0; index < searchArray.length; index++) {
    let term: any = searchArray[index] || '';
    term = term.toString();
    if (term.toLowerCase().indexOf(search.toLowerCase()) !== -1) {
      return true;
    }
  }
  return false;
};

/**
 * Set the width of the filter columns based up the following conditions:
 * - If the component is a checkbox, set column width to be auto, preventing it from taking up too much space.
 * - If displaying only one item, set column width to 0
 * - Else set default column width to 3
 * @param component
 */
const getFilterColProps = (component: any) => {
  const name =
    (component as any).name || (component as any).__name || undefined;

  // If the component is a checkbox, set column width to be auto, preventing it from taking up too much space.
  const isColAuto = name?.includes('BMCheckBox');

  if (isColAuto) return 'auto';

  return props.tableFilters.length === constants.table.MAX_NR_OF_FILTERS_PER_ROW
    ? 0
    : 3;
};

// Check to see if top divider should be displayed based on different conditions
const hasTopContent = computed(() => {
  const hasTitle = Boolean(useSlots().titleSlot);
  const hasActions = Boolean(useSlots().tableActions);
  const hasPostFilter = Boolean(useSlots().postFilterSlot);
  const hasFilters = props.tableFilters?.length > 0;

  return hasTitle || hasActions || hasPostFilter || hasFilters;
});

// The final logic gate
const isTopDividerActive = computed(() => {
  return props.showTopDivider || hasTopContent.value;
});

// Watchers
// Allow parent components to dynamically update sorting (e.g. from query params on keep-alive activation)
watch(
  () => props.initialSort,
  (value) => {
    sortArray.value = value ? [value] : [];
  },
  { immediate: false }
);

watch(
  () => props.headers,
  (newHeaders) => {
    if (
      props.tableId &&
      newHeaders.length > 0 &&
      showColumns.value.length === 0
    ) {
      showColumns.value = newHeaders.map((h) => h.key);
    }
  },
  { immediate: true }
);

// Hooks
if (!props.noDefaultSorting) {
  const key = sortableColumns.value?.[0]?.value ?? '';
  const { ASCENDING, DESCENDING } = constants.table;
  const order = props.initialSortDesc
    ? (DESCENDING as 'desc')
    : (ASCENDING as 'asc');

  sortArray.value = [{ key, order }];
}
if (props.initialSort) sortArray.value = [props.initialSort];
if (props.urlParamsSyncedTableName) applyQueryParams();
</script>

<style scoped lang="scss">
@use '@/styles/helpers/scrollbar.scss';

.bm-table {
  :deep(.v-expansion-panel-title__overlay),
  :deep(.v-expansion-panel__shadow) {
    display: none;
  }

  :deep(.v-expansion-panel-text) {
    background: rgb(var(--v-theme-surface));
    border-top: 1px solid;
    border-bottom: 1px solid;
    border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  }

  :deep(.v-data-table__th--sortable:hover) .hidden-sorting-icon {
    opacity: 0.6;
  }

  :deep(.v-selection-control i) {
    opacity: 1;

    &:before {
      color: rgb(var(--v-theme-primary));
    }
  }

  :deep(.v-progress-linear__indeterminate) {
    background-color: rgb(var(--v-theme-primary)) !important;
  }

  // selected row styles
  // @TODO: Vuetify should ideally provide a better way to target selected rows, this is quite fragile
  :deep(tr.v-data-table__tr:has(.v-selection-control i.mdi-checkbox-marked)) {
    background: #f5f5f5;
    cursor: auto;
  }

  :deep(.v-table__wrapper) {
    @extend %style-scroll-bar;
  }

  // If we inject a "selected" state to the table-row, then we want that row to be selected
  :deep(tr) {
    position: relative;

    &.selected {
      td {
        position: relative;
        z-index: 2;
      }

      &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        background: currentColor;
        opacity: var(--v-selected-opacity);
        z-index: 1;
      }
    }
  }
}

.actions-cell {
  padding-left: 10px;
}

// Mobile two-column layout
.mobile-field.full-width {
  flex: 0 0 100%;
  max-width: 100%;
}
</style>
